import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { adProvider, adUnit, adsLive, loadAdScript } from '../monetization';

/* A clearly labelled ad in a box whose size is fixed by CSS before any ad
   loads (728x90, or 320x100 on phones), so nothing below it ever moves.
   Renders nothing when no network or unit id is configured. The ad is
   requested only when the box nears the viewport. */
function AdSlot({ slot = 'inline' }) {
  const box = useRef(null);
  const unit = adUnit(slot);

  useEffect(() => {
    const el = box.current;
    if (!el || !unit || !adsLive) return undefined;
    let done = false;
    const fill = () => {
      if (done) return;
      done = true;
      loadAdScript()
        .then(() => { if (el.isConnected && !el.firstChild) adProvider.mount(el, unit); })
        .catch(() => {});
    };
    if (!('IntersectionObserver' in window)) { fill(); return undefined; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { io.disconnect(); fill(); }
    }, { rootMargin: '300px 0px' });
    io.observe(el);
    return () => { done = true; io.disconnect(); };
  }, [unit]);

  if (!unit) return null;
  return (
    <aside className={`ad-slot ad-${slot}`} aria-label="Advertisement">
      <span className="ad-label">Advertisement</span>
      <div className={`ad-box${adsLive ? '' : ' ad-dev'}`} ref={box} />
    </aside>
  );
}

/* Keyed on the route so client-side navigation always gets a fresh ad, even
   when the page component itself stays mounted (/brawlers/1 -> /brawlers/2). */
export default function RoutedAdSlot(props) {
  const { pathname } = useLocation();
  return <AdSlot key={pathname} {...props} />;
}
